import { E } from '@angular/cdk/keycodes';
import { inject, Injectable, Renderer2, RendererFactory2, DOCUMENT } from '@angular/core';
import { App } from '@df/core/app';

export namespace NHead {
    /**
     * Placement options for adding elements to the HTML head
     */
    export type TPlacement = 'top' | 'bottom' | { before: HTMLElement } | { after: HTMLElement };

    /**
     * Base configuration for adding an element to the HTML head
     */
    interface IBaseConfig<TElement extends HTMLElement = HTMLElement> {
        /**
         * Optional placement in the head
         * When not specified, elements are automatically ordered as:
         * 1. title
         * 2. meta and base
         * 3. link
         * 4. style
         * 5. script
         *
         * Can be:
         * - 'top': Insert at the very beginning of head
         * - 'beforeScripts': Insert before the first script tag
         * - 'afterScripts': Insert after the last script tag
         * - { before: HTMLElement | selector }: Insert before a specific element
         * - { after: HTMLElement | selector }: Insert after a specific element
         */
        placement?: TPlacement;

        /**
         * If allowed in SSR
         */
        ssr: boolean;
        /**
         * Optional callback when element is created
         */
        onCreate?: (element: TElement) => void;

        /**
         * Optional callback when element loads successfully
         */
        onLoad?: (element: TElement) => void;

        /**
         * Optional callback when element fails to load
         */
        onError?: (element: TElement) => void;

        attributes?: {
            id?: string;
        };
    }

    /**
     * Configuration for adding a meta element
     */
    export interface IMetaConfig extends IBaseConfig<HTMLMetaElement> {
        element: 'meta';
        /**
         * Meta tag attributes
         */
        attributes: {
            id?: string;
            name?: string;
            property?: string;
            content?: string;
            charset?: string;
            'http-equiv'?: string;
            [key: string]: string | undefined;
        };
    }

    /**
     * Configuration for adding a link element
     */
    export interface ILinkConfig extends IBaseConfig<HTMLLinkElement> {
        element: 'link';
        /**
         * Link tag attributes
         */
        attributes: {
            id?: string;
            rel: string;
            href?: string;
            type?: string;
            sizes?: string;
            media?: string;
            hreflang?: string;
            crossorigin?: 'anonymous' | 'use-credentials';
            [key: string]: string | undefined;
        };
    }

    /**
     * Configuration for adding a script element
     */
    export interface IScriptConfig extends IBaseConfig<HTMLScriptElement> {
        element: 'script';
        /**
         * Script tag attributes
         */
        attributes?: {
            id?: string;
            src?: string;
            type?: string;
            async?: string;
            defer?: string;
            crossorigin?: 'anonymous' | 'use-credentials';
            integrity?: string;
            nomodule?: string;
            [key: string]: string | undefined;
        };
        /**
         * Optional inline script content
         */
        textContent?: string;
    }

    /**
     * Configuration for adding a style element
     */
    export interface IStyleConfig extends IBaseConfig<HTMLStyleElement> {
        element: 'style';
        /**
         * Style tag attributes
         */
        attributes?: {
            id?: string;
            type?: string;
            media?: string;
            [key: string]: string | undefined;
        };
        /**
         * CSS content
         */
        textContent?: string;
    }

    /**
     * Configuration for adding a title element
     */
    export interface ITitleConfig extends IBaseConfig<HTMLTitleElement> {
        element: 'title';
        /**
         * Title text content
         */
        textContent?: string;
    }

    /**
     * Configuration for adding a base element
     */
    export interface IBaseElementConfig extends IBaseConfig<HTMLBaseElement> {
        element: 'base';
        /**
         * Base tag attributes
         */
        attributes: {
            id?: string;
            href: string;
            target?: '_blank' | '_self' | '_parent' | '_top';
        };
    }

    /**
     * Configuration for adding a custom HTML element
     */
    export interface ICustomConfig extends Omit<IBaseConfig, 'placement'> {
        /**
         * Custom element (can be an HTMLElement or element tag name)
         */
        element: HTMLElement | string;
        /**
         * Required placement for custom elements
         */
        placement: TPlacement;
        /**
         * Optional attributes to set on the element
         */
        attributes?: { id?: string } & Record<string, string>;
        /**
         * Optional text content for the element
         */
        textContent?: string;
    }

    /**
     * Union type of all head configuration types
     */
    export type TConfig = IMetaConfig | ILinkConfig | IScriptConfig | IStyleConfig | ITitleConfig | IBaseElementConfig | ICustomConfig;

    /**
     * Tag order
     */
    export const TAG_ORDER = ['title', 'base', 'meta', 'link', 'style', 'script'];
}

/**
 * Service for adding elements to the HTML head with controlled placement
 */
@Injectable({
    providedIn: 'root'
})
export class HeadService {
    protected rendererFactory = inject(RendererFactory2);
    protected document = inject(DOCUMENT);
    protected app = inject(App);

    private _renderer2?: Renderer2;
    public get renderer2(): Renderer2 {
        if (!this._renderer2) {
            this._renderer2 = this.rendererFactory.createRenderer(null, null);
        }
        return this._renderer2;
    }

    /**
     * Adds a meta element to the HTML head
     */
    add(config: NHead.IMetaConfig): HTMLMetaElement;
    /**
     * Adds a link element to the HTML head
     */
    add(config: NHead.ILinkConfig): HTMLLinkElement;
    /**
     * Adds a script element to the HTML head
     */
    add(config: NHead.IScriptConfig): HTMLScriptElement;
    /**
     * Adds a style element to the HTML head
     */
    add(config: NHead.IStyleConfig): HTMLStyleElement;
    /**
     * Adds a title element to the HTML head
     */
    add(config: NHead.ITitleConfig): HTMLTitleElement;
    /**
     * Adds a base element to the HTML head
     */
    add(config: NHead.IBaseElementConfig): HTMLBaseElement;
    /**
     * Adds a custom element to the HTML head
     */
    add(config: NHead.ICustomConfig): HTMLElement;
    /**
     * Adds an element to the HTML head with specified placement
     *
     * @param config - Configuration for adding the element
     * @returns The added HTML element with the correct type
     */
    add(config: NHead.TConfig): HTMLElement | undefined {
        const { element, attributes, textContent, onLoad, onError } = config as any;

        if (!config.ssr && !this.app.isPlatformBrowser) {
            return undefined;
        }

        // Create or use provided element
        let el: HTMLElement;
        let tagName: string | undefined;
        if (typeof element === 'string') {
            tagName = element;
            const id = config.attributes?.id;
            const existingElement = id && this.findElement(`#${id}`);
            el = existingElement || this.renderer2.createElement(element);

            // WebKit hack
            if (element === 'style' && !existingElement) {
                this.renderer2.appendChild(el, this.document.createTextNode(''));
            }

            // Set attributes if provided
            if (attributes) {
                Object.keys(attributes).forEach(key => {
                    const value = attributes[key];
                    if (value !== undefined) {
                        this.renderer2.setAttribute(el, key, value);
                    }
                });
            }

            // Automatically add ssr attribute when not in browser mode
            if (!this.app.isPlatformBrowser) {
                this.renderer2.setAttribute(el, 'ssr', 'true');
            }
            this.renderer2.setAttribute(el, 'ctx', 'df');

            // Set text content if provided
            if (textContent) {
                this.renderer2.setProperty(el, 'textContent', textContent);
            }

            // Set load/error callbacks if provided
            if (onLoad) {
                el.onload = () => onLoad(el);
            }
            if (onError) {
                el.onerror = () => onError(el);
            }
        } else {
            el = element;
            tagName = element.tagName.toLowerCase();
        }

        if (config.onCreate) {
            config.onCreate(el as any);
        }

        const elementToPlaceBefore = tagName ? this.getELementToPlaceBefore(tagName) : undefined;
        if (elementToPlaceBefore) {
            this.renderer2.insertBefore(this.document.head, el, elementToPlaceBefore);
        } else {
            this.renderer2.appendChild(this.document.head, el);
        }

        return el;
    }

    /**
     * Determines the default placement for an element type
     * If undefined is returned, the element will be placed at the end of the head
     */
    private getELementToPlaceBefore(tagType: string): Node | undefined {
        const head = this.document.head;

        const tagOrder = NHead.TAG_ORDER;
        const index = tagOrder.indexOf(tagType);
        // if last put at the end
        if (index === tagOrder.length - 1) {
            return undefined;
        }

        let beforeElement = head.firstChild || undefined;
        // if first put at the beginning
        if (index < 1) {
            return beforeElement;
        }

        const tagsBefore = tagOrder.slice(0, index);
        const tagsAfter = tagOrder.slice(index + 1);

        if (!tagsAfter.length) {
            return undefined;
        }

        head.childNodes.forEach(child => {
            if (child instanceof HTMLElement && child.getAttribute('ctx') === 'df') {
                const tagName = (child as HTMLElement).tagName?.toLowerCase();
                // if child needs to be before it, we need to reset last found element
                // if child needs to be after it, we can assign as first found to place before it
                if (tagsAfter.includes(tagName)) {
                    return beforeElement;
                }
                beforeElement = child;
            }
        });

        return beforeElement;
    }

    /**
     * Removes an element from the head
     *
     * @param element - The element to remove
     */
    remove(element: HTMLElement): void {
        if (element && element.parentNode === this.document.head) {
            this.renderer2.removeChild(this.document.head, element);
        }
    }

    /**
     * Finds an existing element in the head
     * Only works in browser mode to avoid SSR hydration issues
     *
     * @param selector - CSS selector string to find the element
     * @returns The found element or null
     */
    findElement<T extends HTMLElement = HTMLElement>(selector: string): T | null {
        if (this.document.head) {
            return this.document.head.querySelector<T>(selector);
        }
        return null;
    }

    /**
     * Finds all existing elements in the head matching the selector
     * Only works in browser mode to avoid SSR hydration issues
     *
     * @param selector - CSS selector string to find elements
     * @returns Array of found elements
     */
    findElements<T extends HTMLElement = HTMLElement>(selector: string): T[] {
        if (this.document.head) {
            return Array.from(this.document.head.querySelectorAll<T>(selector));
        }
        return [];
    }
}
